// ─────────────────────────────────────────────────────────────────────────────
// SchoolReports.jsx
// A class-wise roll overview. The landing view is a table of classes with their
// active / inactive head-counts; clicking a class drills into that class's
// students, filterable by Active / Inactive.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState, useEffect } from "react";
import { C, fmtClass, fmtSection, Card, CardHeader, TH, TD, Badge, Avatar, Btn, Icon } from "../fees/feeUI";
import { normalizeStudents, statusColor, StatCard, StatGrid, PageHeading, BackButton, ReportToolbar, Pagination, useAdminCampus } from "./reportsShared";
import { printReport, exportCsv } from "./reportExport";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import {
  classSectionsOf,
  classSessionIdsOf,
  filterClassesBySession,
  sectionsForClassSession,
} from "../../../utils/campusClasses";

const PER_PAGE = 12;
const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: 700,
  color: C.slate,
  marginBottom: "5px",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
};
const inputStyle = {
  padding: "9px 13px",
  borderRadius: "8px",
  border: `1.5px solid ${C.border}`,
  fontSize: "13px",
  color: C.text,
  background: C.white,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

// ── Campus picker (superadmin landing view) ───────────────────────────────────
const CampusPicker = ({ campuses, isLoading, error, onRetry, onSelect }) => {
  if (isLoading) {
    return <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>Loading campuses…</div>;
  }
  if (error) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div style={{ color: C.red, fontWeight: 700, marginBottom: "14px" }}>{error}</div>
        <Btn onClick={onRetry}>Retry</Btn>
      </div>
    );
  }
  if (campuses.length === 0) {
    return <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>No campuses found.</div>;
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
      {campuses.map(c => (
        <Card key={c.id}>
          <div style={{ padding: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
            <Avatar name={c.name} />
            <div>
              <div style={{ fontWeight: 700, fontSize: "15px", color: C.text }}>{c.name}</div>
              <div style={{ fontSize: "12px", color: C.slate, marginTop: "2px" }}>{c.location || "No location set"}</div>
            </div>
          </div>
          <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "center" }}>
            <Btn onClick={() => onSelect(c)} style={{ width: "100%", justifyContent: "center" }}>View Report</Btn>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ── Class detail (drill-down) ─────────────────────────────────────────────────
const ClassDetail = ({ className, students, campus, onBack }) => {
  const [tab, setTab]   = useState("all"); // all | active | inactive
  const [page, setPage] = useState(1);

  const active   = students.filter(s => s.status === "Active");
  const inactive = students.filter(s => s.status !== "Active");
  const shown    = tab === "active" ? active : tab === "inactive" ? inactive : students;

  useEffect(() => { setPage(1); }, [tab]);
  const safePage  = Math.min(page, Math.max(1, Math.ceil(shown.length / PER_PAGE)));
  const pageRows  = shown.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const TABS = [
    { id: "all",      label: `All (${students.length})` },
    { id: "active",   label: `Active (${active.length})` },
    { id: "inactive", label: `Inactive (${inactive.length})` },
  ];

  // Report config follows the current Active/Inactive toggle.
  const reportConfig = () => ({
    title: `Class Report — ${fmtClass(className)}`,
    campus,
    scopeMeta: [["Class", fmtClass(className)], ["View", tab === "all" ? "All" : tab === "active" ? "Active" : "Inactive"], ["Students", shown.length]],
    summaryCards: [
      { label: "Total", value: students.length },
      { label: "Active", value: active.length },
      { label: "Inactive", value: inactive.length },
    ],
    sections: [{
      heading: `${fmtClass(className)} — Students`,
      headers: ["#", "Student", "Roll No.", "Section", "Status"],
      rows: shown.map((s, i) => [i + 1, s.name, s.rollNo, fmtSection(s.section), s.status]),
    }],
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "4px" }}>
        <BackButton onClick={onBack} label="Back to classes" />
        <div style={{ marginBottom: "16px" }}>
          <ReportToolbar onExport={() => exportCsv({ filename: `class-${fmtClass(className).replace(/\s+/g, "-").toLowerCase()}.csv`, ...reportConfig() })} onPrint={() => printReport(reportConfig())} />
        </div>
      </div>

      <StatGrid>
        <StatCard label="Total Students" value={students.length} accent={C.navy} />
        <StatCard label="Active"   value={active.length}   sub="currently enrolled"           accent={C.green} />
        <StatCard label="Inactive" value={inactive.length} sub="inactive / withdrawn / left"  accent={C.red} />
      </StatGrid>

      <Card>
        <CardHeader title={`${fmtClass(className)} — Students`} right={<Badge color="slate">{shown.length} shown</Badge>} />

        {/* Active / Inactive toggle */}
        <div style={{ display: "flex", gap: "6px", padding: "12px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "7px 14px", borderRadius: "8px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer",
              border: tab === t.id ? "none" : `1px solid ${C.border}`,
              background: tab === t.id ? C.navy : C.white,
              color: tab === t.id ? "#fff" : C.slate,
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><TH>#</TH><TH>Student</TH><TH>Roll No.</TH><TH>Section</TH><TH>Status</TH></tr></thead>
            <tbody>
              {shown.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: "48px", textAlign: "center", color: C.slate, fontSize: "13px" }}>No students in this group.</td></tr>
              ) : pageRows.map((s, i) => (
                <tr key={s.id} style={{ background: i % 2 === 0 ? C.white : C.bg }}>
                  <TD><span style={{ color: C.slateLight, fontWeight: 600 }}>{(safePage - 1) * PER_PAGE + i + 1}</span></TD>
                  <TD>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar name={s.name} />
                      <span style={{ fontWeight: 700 }}>{s.name}</span>
                    </div>
                  </TD>
                  <TD mono>{s.rollNo}</TD>
                  <TD>{fmtSection(s.section)}</TD>
                  <TD><Badge color={statusColor(s.status)}>{s.status}</Badge></TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={safePage} perPage={PER_PAGE} total={shown.length} onChange={setPage} />
      </Card>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const SchoolReports = ({ students = [] }) => {
  const isSuperadmin = (localStorage.getItem("als_role") || "").toLowerCase() === "superadmin";

  const [campuses, setCampuses] = useState([]);
  const [campusesLoading, setCampusesLoading] = useState(true);
  const [campusesError, setCampusesError] = useState("");
  const [selectedCampus, setSelectedCampus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const [sessionFilter, setSessionFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [studentFilter, setStudentFilter] = useState("all");

  const fetchCampuses = () => {
    setCampusesLoading(true);
    setCampusesError("");
    fetch(`${API_BASE_URL}/api/campuses`, { headers: getAuthHeaders() })
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data) => {
        const campusList = Array.isArray(data) ? data : [];
        setCampuses(campusList);
        setSelectedCampus((prev) => {
          if (prev) {
            return campusList.find((campus) => String(campus.id) === String(prev.id)) || prev;
          }
          if (!isSuperadmin) return campusList[0] || null;
          return null;
        });
      })
      .catch(() => setCampusesError("Could not load campuses."))
      .finally(() => setCampusesLoading(false));
  };

  useEffect(() => { fetchCampuses(); }, [isSuperadmin]);

  const [selectedClass, setSelectedClass] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSelectedClass(null);
    setPage(1);
    setSessionFilter("");
    setClassFilter("");
    setSectionFilter("");
    setStudentFilter("all");
  }, [selectedCampus]);

  useEffect(() => {
    if (!selectedCampus?.id) {
      setSessions([]);
      setClasses([]);
      return;
    }

    let alive = true;
    setFiltersLoading(true);

    Promise.all([
      fetch(`${API_BASE_URL}/api/sessions?campus_id=${selectedCampus.id}`, { headers: getAuthHeaders() }),
      fetch(`${API_BASE_URL}/api/classes?campus_id=${selectedCampus.id}`, { headers: getAuthHeaders() }),
    ])
      .then(async ([sessionsRes, classesRes]) => {
        if (!sessionsRes.ok || !classesRes.ok) throw new Error();
        const [sessionsData, classesData] = await Promise.all([sessionsRes.json(), classesRes.json()]);
        if (!alive) return;
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setClasses(Array.isArray(classesData) ? classesData : []);
      })
      .catch(() => {
        if (!alive) return;
        setSessions([]);
        setClasses([]);
      })
      .finally(() => {
        if (alive) setFiltersLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [selectedCampus]);

  const scopedStudents = useMemo(
    () => (selectedCampus ? students.filter((s) => String(s.campus_id) === String(selectedCampus.id)) : students),
    [students, selectedCampus],
  );

  const norm = useMemo(() => normalizeStudents(scopedStudents), [scopedStudents]);

  const classMap = useMemo(() => {
    return new Map(classes.map((classItem) => [String(classItem.id), classItem]));
  }, [classes]);

  const sessionMap = useMemo(() => {
    return new Map(sessions.map((session) => [String(session.id), session]));
  }, [sessions]);

  const availableClasses = useMemo(
    () => filterClassesBySession(classes, sessionFilter),
    [classes, sessionFilter],
  );

  const availableSections = useMemo(
    () =>
      sectionsForClassSession(classes, {
        sessionId: sessionFilter,
        className: classFilter,
        students: norm,
        formatSection: fmtSection,
      }),
    [classes, sessionFilter, classFilter, norm],
  );

  const filteredStudents = useMemo(() => {
    return norm.filter((student) => {
      const linkedClass = classMap.get(String(student.class_id || ""));
      const linkedSessionIds = linkedClass ? classSessionIdsOf(linkedClass) : [];
      const linkedSessionNames = linkedSessionIds
        .map((id) => sessionMap.get(String(id))?.name)
        .filter(Boolean);
      const studentSession = String(student.academic_session || "").trim();
      const matchesSession = !sessionFilter || (
        linkedSessionIds.includes(String(sessionFilter)) ||
        linkedSessionNames.some(
          (name) => String(name || "").trim() === String(sessionMap.get(String(sessionFilter))?.name || "").trim(),
        ) ||
        studentSession === String(sessionMap.get(String(sessionFilter))?.name || "").trim()
      );
      const matchesClass = !classFilter || String(student.class || "") === String(classFilter);
      const matchesSection = !sectionFilter || fmtSection(student.section) === String(sectionFilter);

      const status = String(student.status || "").toLowerCase();
      const matchesStudentFilter =
        studentFilter === "all" ||
        (studentFilter === "withdrawn" && status === "withdrawn") ||
        (studentFilter === "promoted" && (status === "graduated" || status === "promoted")) ||
        (studentFilter === "active" && status === "active");

      return matchesSession && matchesClass && matchesSection && matchesStudentFilter;
    });
  }, [classFilter, classMap, norm, sectionFilter, sessionFilter, sessionMap, studentFilter]);

  const classRows = useMemo(() => {
    const map = new Map();
    filteredStudents.forEach((s) => {
      const cls = s.class || "—";
      if (!map.has(cls)) map.set(cls, { cls, total: 0, active: 0, withdrawn: 0, sections: new Set() });
      const r = map.get(cls);
      r.total += 1;
      if (s.status === "Active") r.active += 1;
      if (s.status === "Withdrawn") r.withdrawn += 1;
      if (s.section) r.sections.add(fmtSection(s.section));
    });
    return [...map.values()]
      .map(r => ({ ...r, sections: [...r.sections].sort() }))
      .sort((a, b) => String(a.cls).localeCompare(String(b.cls)));
  }, [filteredStudents]);

  const totals = useMemo(() => ({
    total: filteredStudents.length,
    active: filteredStudents.filter((s) => s.status === "Active").length,
    withdrawn: filteredStudents.filter((s) => s.status === "Withdrawn").length,
    promoted: filteredStudents.filter((s) => ["Graduated", "Promoted"].includes(s.status)).length,
    classes:   classRows.length,
  }), [filteredStudents, classRows.length]);

  const selectedStudents = useMemo(
    () => (selectedClass ? filteredStudents.filter((s) => (s.class || "—") === selectedClass) : []),
    [filteredStudents, selectedClass]
  );

  const adminCampus = useAdminCampus();
  const campus = isSuperadmin ? (selectedCampus?.name || "All Campuses") : (selectedCampus?.name || adminCampus);

  const classPage = Math.min(page, Math.max(1, Math.ceil(classRows.length / PER_PAGE)));

  const filterMeta = [
    ["Session", sessionFilter ? (sessionMap.get(String(sessionFilter))?.name || "Selected") : "All"],
    ["Class", classFilter || "All"],
    ["Section", sectionFilter || "All"],
    ["Student Filter", studentFilter === "all"
      ? "All"
      : studentFilter === "withdrawn"
        ? "Withdrawn"
        : studentFilter === "promoted"
          ? "Promoted / Graduated"
          : "Active"],
  ];

  const classesReport = () => ({
    title: "School Report — Classes",
    campus,
    scopeMeta: [...filterMeta, ["Classes", classRows.length], ["Total Students", totals.total]],
    summaryCards: [
      { label: "Total Students", value: totals.total },
      { label: "Active", value: totals.active },
      { label: "Withdrawn", value: totals.withdrawn },
      { label: "Promoted", value: totals.promoted },
      { label: "Classes", value: totals.classes },
    ],
    sections: [{
      heading: "Class-wise Roll (Active / Withdrawn)",
      headers: ["Class", "Section", "Active", "Withdrawn", "Total"],
      rightCols: ["Active", "Withdrawn", "Total"],
      rows: classRows.map(r => [fmtClass(r.cls), r.sections.join(", ") || "—", r.active, r.withdrawn, r.total]),
    }],
  });

  if (isSuperadmin && !selectedCampus) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
        <PageHeading title="School Reports" subtitle="Select a campus to view its report" />
        <CampusPicker
          campuses={campuses}
          isLoading={campusesLoading}
          error={campusesError}
          onRetry={fetchCampuses}
          onSelect={setSelectedCampus}
        />
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      {isSuperadmin && !selectedClass && (
        <div style={{ marginBottom: "4px" }}>
          <BackButton onClick={() => setSelectedCampus(null)} label="Back to campuses" />
        </div>
      )}

      <PageHeading
        title={isSuperadmin ? `School Report — ${selectedCampus.name}` : undefined}
        actions={selectedClass ? null : <ReportToolbar onExport={() => exportCsv({ filename: "school-report-classes.csv", ...classesReport() })} onPrint={() => printReport(classesReport())} />}
      />

      {selectedClass ? (
        <ClassDetail className={selectedClass} students={selectedStudents} campus={campus} onBack={() => setSelectedClass(null)} />
      ) : (
        <>
          <StatGrid>
            <StatCard label="Total Students" value={totals.total}     accent={C.navy} />
            <StatCard label="Active"         value={totals.active}    sub={`${totals.total ? Math.round((totals.active / totals.total) * 100) : 0}% of school`} accent={C.green} />
            <StatCard label="Withdrawn"      value={totals.withdrawn} sub="left the school" accent={C.red} />
            <StatCard label="Promoted"       value={totals.promoted}  sub="graduated / promoted records" accent={C.blue} />
            <StatCard label="Classes"        value={totals.classes}   accent={C.amber} />
          </StatGrid>

          <Card style={{ marginBottom: "16px" }}>
            <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", alignItems: "end" }}>
              <div>
                <label style={labelStyle}>Session</label>
                <select
                  value={sessionFilter}
                  onChange={(e) => {
                    setSessionFilter(e.target.value);
                    setClassFilter("");
                    setSectionFilter("");
                    setPage(1);
                  }}
                  style={inputStyle}
                  disabled={filtersLoading}
                >
                  <option value="">All sessions</option>
                  {sessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Class</label>
                <select
                  value={classFilter}
                  onChange={(e) => {
                    setClassFilter(e.target.value);
                    setSectionFilter("");
                    setPage(1);
                  }}
                  style={inputStyle}
                  disabled={filtersLoading}
                >
                  <option value="">All classes</option>
                  {availableClasses.map((classItem) => (
                    <option key={classItem.id} value={classItem.name}>
                      {fmtClass(classItem.name)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Section</label>
                <select
                  value={sectionFilter}
                  onChange={(e) => {
                    setSectionFilter(e.target.value);
                    setPage(1);
                  }}
                  style={inputStyle}
                  disabled={filtersLoading}
                >
                  <option value="">All sections</option>
                  {availableSections.map((section) => (
                    <option key={section} value={section}>
                      Section {section}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Student Filter</label>
                <select
                  value={studentFilter}
                  onChange={(e) => {
                    setStudentFilter(e.target.value);
                    setPage(1);
                  }}
                  style={inputStyle}
                >
                  <option value="all">All students</option>
                  <option value="active">Active students</option>
                  <option value="withdrawn">Withdrawn students</option>
                  <option value="promoted">Promoted / Graduated students</option>
                </select>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Classes" right={<Badge color="slate">{classRows.length} class{classRows.length === 1 ? "" : "es"}</Badge>} />
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr><TH>Class</TH><TH>Section</TH><TH right>Active</TH><TH right>Withdrawn</TH><TH right>Total</TH><TH></TH></tr>
                </thead>
                <tbody>
                  {classRows.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: "48px", textAlign: "center", color: C.slate, fontSize: "13px" }}>No students found for the selected filters.</td></tr>
                  ) : classRows.slice((classPage - 1) * PER_PAGE, classPage * PER_PAGE).map((r, i) => {
                    const rowBg = i % 2 === 0 ? C.white : C.bg;
                    return (
                      <tr key={r.cls} style={{ background: rowBg, cursor: "pointer" }}
                        onClick={() => setSelectedClass(r.cls)}
                        title="Click to view this class's students"
                        onMouseEnter={e => { e.currentTarget.style.background = C.blueSoft; }}
                        onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                        <TD><Badge color="blue">{fmtClass(r.cls)}</Badge></TD>
                        <TD>{r.sections.length ? r.sections.join(", ") : "—"}</TD>
                        <TD right><span style={{ color: C.green, fontWeight: 700 }}>{r.active}</span></TD>
                        <TD right><span style={{ color: r.withdrawn > 0 ? C.red : C.slateLight, fontWeight: 700 }}>{r.withdrawn}</span></TD>
                        <TD right><strong>{r.total}</strong></TD>
                        <TD right><span style={{ color: C.slateLight, display: "inline-flex" }}><Icon.ArrowRight /></span></TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={classPage} perPage={PER_PAGE} total={classRows.length} onChange={setPage} />
          </Card>
        </>
      )}
    </div>
  );
};

export default SchoolReports;
